import type { Frame } from "../renderer/Frame";
import type { DPoint } from "./types";

export interface SnapResult { point: DPoint; label: string | null; x: number; y: number }

const TOL = 8;

// Magnet: snap the cursor to candle O/H/L/C, swing highs/lows, BOS/CHoCH levels and FVG / order-block edges.
export function snapPoint(f: Frame, x: number, y: number, enabled: boolean): SnapResult {
  const raw: DPoint = { time: f.timeAt(x), price: f.priceAt(y) };
  if (!enabled) return { point: raw, label: null, x, y };
  const idx = Math.round(f.indexAt(x));
  const c = f.candles[idx];
  const cands: { price: number; label: string; time?: number }[] = [];
  if (c) {
    cands.push({ price: c.open, label: "O", time: c.time }, { price: c.high, label: "H", time: c.time },
      { price: c.low, label: "L", time: c.time }, { price: c.close, label: "C", time: c.time });
    const prev = f.candles[idx - 1];
    if (prev) cands.push({ price: prev.high, label: "prev H", time: c.time }, { price: prev.low, label: "prev L", time: c.time });
  }
  const s = f.opts.structure;
  if (s) {
    for (const h of s.highs) if (Math.abs(h.index - idx) <= 3) cands.push({ price: h.price, label: "Swing high", time: f.candles[h.index]?.time });
    for (const l of s.lows) if (Math.abs(l.index - idx) <= 3) cands.push({ price: l.price, label: "Swing low", time: f.candles[l.index]?.time });
    for (const ev of s.events) if (ev.from <= f.end && ev.index >= f.start - 5) cands.push({ price: ev.price, label: `${ev.type} level` });
    for (const g of s.fvg) if (g.index >= f.start - 20 && g.index <= f.end) cands.push({ price: g.top, label: "FVG top" }, { price: g.bottom, label: "FVG bottom" });
    for (const b of s.obs) if (b.index >= f.start - 20 && b.index <= f.end) cands.push({ price: b.top, label: "OB top" }, { price: b.bottom, label: "OB bottom" });
  }
  let best: (typeof cands)[number] | null = null, bestD = TOL + 1;
  for (const k of cands) {
    const d = Math.abs(f.y(k.price) - y);
    if (d < bestD) { bestD = d; best = k; }
  }
  if (!best) return { point: raw, label: null, x, y };
  const time = best.time ?? (c ? c.time : raw.time);
  return { point: { time, price: best.price }, label: best.label, x: f.xt(time), y: f.y(best.price) };
}
